import { useEffect, useState } from 'react';

import {
  AppScreen,
  Fab,
  JournalEntryCard,
  SectionHeaderRow,
  StatusBar,
  TabBar,
} from './kit';
import type { JournalEntryCardProps } from './kit';
import { PhoneFrame } from './PhoneFrame';
import { prefersReducedMotion, useInView } from './useInView';
import './JournalMotionDemo.css';

const TYPING_START_MS = 900;
const CHAR_MS = 25;

const TOP_ENTRY: JournalEntryCardProps = {
  weekday: 'FRI',
  dayNumber: 6,
  title: 'Medical',
  body: "Owen's checkup went well. He's behind on one vaccine, scheduled for the 20th. Dr. Patel was g…",
  time: '3:55 AM',
  chipColor: '#E5645A',
  chipLabel: 'Medical',
};

const OTHER_ENTRIES: ReadonlyArray<JournalEntryCardProps> = [
  {
    weekday: 'THU',
    dayNumber: 5,
    title: 'Medical',
    body: 'Owen has his 4 year checkup tomorrow at 10am with Dr. Patel. Texted Jose to let him know.',
    time: '3:55 AM',
    chipColor: '#E5645A',
    chipLabel: 'Medical',
  },
  {
    weekday: 'TUE',
    dayNumber: 3,
    title: 'Custody Exchange',
    body: "Picked up the kids from Jose's at 5pm. Lily said she didn't have lunch today, just a snack on the…",
    time: '3:55 AM',
    chipColor: '#018B9C',
    chipLabel: 'Custody Exch…',
  },
];

export function JournalMotionDemo() {
  const { ref, seen } = useInView<HTMLDivElement>({ threshold: 0.25 });
  const [reduce] = useState(() => prefersReducedMotion());
  const [typed, setTyped] = useState(0);

  useEffect(() => {
    if (!seen || reduce) return;

    let typing: number | undefined;
    const start = window.setTimeout(() => {
      typing = window.setInterval(() => {
        setTyped((count) => {
          if (count >= TOP_ENTRY.body.length) {
            if (typing) window.clearInterval(typing);
            return count;
          }
          return count + 1;
        });
      }, CHAR_MS);
    }, TYPING_START_MS);

    return () => {
      window.clearTimeout(start);
      if (typing) window.clearInterval(typing);
    };
  }, [seen, reduce]);

  const topBody = reduce
    ? TOP_ENTRY.body
    : TOP_ENTRY.body.slice(0, typed);
  const typing = seen && !reduce && typed < TOP_ENTRY.body.length;

  return (
    <PhoneFrame size='md'>
      <AppScreen size='md'>
        <div
          ref={ref}
          className={`jm-screen${seen ? ' is-seen' : ''}${
            reduce ? ' is-reduced' : ''
          }`}
        >
          <StatusBar time='9:41' />

          <header className='jm-header'>
            <span className='jm-header__title'>Journal</span>
          </header>

          <div className='jm-intro'>
            <p className='jm-subtitle'>
              Private to you — not shared with your co-parent
            </p>
            <div className='jm-section'>
              <SectionHeaderRow title='All entries' />
            </div>
            <p className='jm-month'>March 2026</p>
          </div>

          <div className='jm-list'>
            <div
              className={`jm-entry jm-entry--1${typing ? ' is-typing' : ''}`}
            >
              <JournalEntryCard
                weekday={TOP_ENTRY.weekday}
                dayNumber={TOP_ENTRY.dayNumber}
                title={TOP_ENTRY.title}
                body={topBody}
                time={TOP_ENTRY.time}
                chipColor={TOP_ENTRY.chipColor}
                chipLabel={TOP_ENTRY.chipLabel}
              />
            </div>

            {OTHER_ENTRIES.map((entry, index) => (
              <div
                key={entry.dayNumber}
                className={`jm-entry jm-entry--${index + 2}`}
              >
                <JournalEntryCard
                  weekday={entry.weekday}
                  dayNumber={entry.dayNumber}
                  title={entry.title}
                  body={entry.body}
                  time={entry.time}
                  chipColor={entry.chipColor}
                  chipLabel={entry.chipLabel}
                />
              </div>
            ))}
          </div>

          <Fab />
          <TabBar active='note' />
        </div>
      </AppScreen>
    </PhoneFrame>
  );
}
